import { NavigationIcons } from 'Components/icons/navigation/enIcons';
import { useRouter } from 'vue-router';
export default function useModuleNavigation({ moduleGroups, expandedChildGroups, displayName, childKey, groupKey, subgroupIcon, groupIcon, isModuleActive, }) {
    const EXPANDED_CACHE_KEY = 'en_nav_expanded';
    const router = useRouter();
    /** Check if a subgroup contains the active module
     * @param subgroup - The subgroup to check
     * @returns True if the subgroup has an active module, false otherwise
    */
    function subgroupHasActiveModule(subgroup) {
        if (!subgroup)
            return false;
        if (Array.isArray(subgroup.ModuleConfigs) && subgroup.ModuleConfigs.some(m => isModuleActive && isModuleActive(m)))
            return true;
        if (Array.isArray(subgroup.ModuleConfigGroups)) {
            return subgroup.ModuleConfigGroups.some(g => subgroupHasActiveModule(g));
        }
        return false;
    }
    /**
     * Expand subgroups that contain the active module so user keeps context.
     * Collapses subgroups that no longer contain the active module.
     */
    function expandToActive() {
        try {
            (moduleGroups?.value || []).forEach(group => {
                if (!group || !Array.isArray(group.ModuleConfigGroups))
                    return;
                group.ModuleConfigGroups.forEach(subgroup => {
                    const key = childKey(groupKey(group), subgroup);
                    if (subgroupHasActiveModule(subgroup)) {
                        (expandedChildGroups.value || new Set()).add(key);
                    }
                    else {
                        expandedChildGroups.value?.delete(key);
                    }
                });
            });
        }
        catch (e) {
            console.error('Error expanding to active module:', e);
        }
    }
    /**
     * Save the current expanded state of child groups to sessionStorage
     * This allows the state to persist across page reloads
     */
    function saveExpandedState() {
        try {
            const arr = Array.from(expandedChildGroups?.value || []);
            sessionStorage.setItem(EXPANDED_CACHE_KEY, JSON.stringify(arr));
        }
        catch (e) {
            console.error('Error saving expanded state:', e);
        }
    }
    /**
     * Restore the expanded state from sessionStorage
     * @returns
     */
    function restoreExpandedState() {
        try {
            const raw = sessionStorage.getItem(EXPANDED_CACHE_KEY);
            if (!raw)
                return;
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && expandedChildGroups) {
                expandedChildGroups.value = new Set(parsed);
            }
            // Remove cache after restore so it doesn't reapply unexpectedly
            sessionStorage.removeItem(EXPANDED_CACHE_KEY);
        }
        catch (e) {
            console.error('Error restoring expanded state:', e);
        }
    }
    // Ensure a parent subgroup is expanded (match by id/area/name/display)
    function ensureParentExpanded(parent) {
        if (!parent)
            return;
        try {
            const groups = moduleGroups?.value || [];
            for (const group of groups) {
                if (!group)
                    continue;
                if (group === parent)
                    return;
                const subgroups = Array.isArray(group.ModuleConfigGroups) ? group.ModuleConfigGroups : [];
                for (const subgroup of subgroups) {
                    const sameId = subgroup?.Id && parent?.Id && subgroup.Id === parent.Id;
                    const sameArea = subgroup?.Area && parent?.Area && subgroup.Area === parent.Area;
                    const sameName = subgroup?.Name && parent?.Name && subgroup.Name === parent.Name;
                    const sameDisplay = displayName && displayName(subgroup) === displayName(parent);
                    if (sameId || sameArea || sameName || sameDisplay) {
                        const key = childKey(groupKey(group), parent);
                        (expandedChildGroups.value || new Set()).add(key);
                        try {
                            saveExpandedState();
                        }
                        catch (e) {
                            console.error('Error saving expanded state:', e);
                        }
                        return;
                    }
                }
            }
        }
        catch (e) {
            console.error('Error ensuring parent expanded:', e);
        }
    }
    /**
     * Build rail entries for subgroup
     * @param {*} group
     * @returns
     */
    function buildRailEntries(group) {
        if (!group)
            return [];
        if (Array.isArray(group.ModuleConfigGroups) && group.ModuleConfigGroups.length) {
            return group.ModuleConfigGroups.map(subgroup => ({
                id: subgroup.Id || subgroup.Name || subgroup.PermissionName || null,
                label: displayName ? displayName(subgroup) : (subgroup.Name || subgroup.Name),
                iconClass: subgroupIcon ? subgroupIcon(subgroup) : NavigationIcons.OTHER,
                active: () => subgroupHasActiveModule(subgroup),
                modules: Array.isArray(subgroup.ModuleConfigs) ? subgroup.ModuleConfigs : [],
                parent: subgroup
            }));
        }
        const groupModules = Array.isArray(group.ModuleConfigs) ? group.ModuleConfigs : [];
        const iconClass = (groupModules.length === 1)
            ? (subgroupIcon ? subgroupIcon(groupModules[0]) : 'mdi mdi-cube-outline')
            : (groupIcon ? groupIcon(group) : 'mdi mdi-folder-outline');
        return [{
                id: group.Id || group.Name || group.Area || null,
                label: displayName ? displayName(group) : (group.Name || group.Name),
                iconClass,
                active: () => subgroupHasActiveModule(group),
                modules: groupModules,
                parent: group
            }];
    }
    let removeAfterEach = null;
    /**
     * Register router.afterEach to expand/collapse subgroups after route settles.
     * Fires after Vue Router has updated route.path — avoids stale-route race condition.
     */
    function setupHistoryListeners() {
        removeAfterEach = router.afterEach(() => { expandToActive(); });
    }
    /**
     * Tear down the router.afterEach guard.
     */
    function teardownHistoryListeners() {
        try {
            if (removeAfterEach) {
                removeAfterEach();
                removeAfterEach = null;
            }
        }
        catch (e) {
            console.error('Error tearing down history listeners:', e);
        }
    }
    return {
        subgroupHasActiveModule,
        expandToActive,
        ensureParentExpanded,
        buildRailEntries,
        saveExpandedState,
        restoreExpandedState,
        setupHistoryListeners,
        teardownHistoryListeners,
    };
}
